import { Component, Injectable, OnInit, ViewChild } from '@angular/core';
import { TransaccionService } from '../../services/transaccion.service';
import { Transaccion } from '../../interfaces/transaccion.interface';
import { MatTableDataSource } from '@angular/material/table';
import { Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { CurrencyPipe } from '@angular/common';
import { RejectTransactionWithdrawModalComponent } from './reject-transaction-withdraw-modal/reject-transaction-withdraw-modal.component';
import { SendTransactionWithdrawModalComponent } from './send-transaction-withdraw-modal/send-transaction-withdraw-modal.component';
import { PayShinkansenModalComponent } from './pay-shinkansen-modal/pay-shinkansen-modal.component';
import { ShinkansenMontoMaxModalComponent } from './shinkansen-monto-max-modal/shinkansen-monto-max-modal.component';
import { Clipboard } from '@angular/cdk/clipboard';
import Swal from 'sweetalert2';
import { MatPaginator, MatPaginatorIntl } from '@angular/material/paginator';
import { SpinnerService } from 'src/app/shared/services/spinner.service';
import { AddWithdrawAccountsComponent } from '../add-withdraw-accounts/add-withdraw-accounts.component';
import { ShinkansenPayoutService } from '../../services/shinkansen-payout.service';
import { WalletVesAdminService } from '../../services/wallet-ves-admin.service';
import { forkJoin, firstValueFrom } from 'rxjs';

@Injectable()
export class CustomPaginatorIntl extends MatPaginatorIntl {
  override itemsPerPageLabel = 'Elementos por página';
  override nextPageLabel = 'Siguiente';
  override previousPageLabel = 'Anterior';
  override firstPageLabel = "Primera página";
  override lastPageLabel = "Última página"

  override getRangeLabel = (page: number, pageSize: number, length: number) => {
    if (length === 0 || pageSize === 0) {
      return `0 de ${length}`;
    }
    length = Math.max(length, 0);
    const startIndex = page * pageSize;
    const endIndex = startIndex < length ?
      Math.min(startIndex + pageSize, length) :
      startIndex + pageSize;
    return `${startIndex + 1} - ${endIndex} de ${length}`;
  };

}
@Component({
  selector: 'app-withdraw-trx-list',
  templateUrl: './withdraw-trx-list.component.html',
  styleUrls: ['./withdraw-trx-list.component.sass']
})
export class WithdrawTrxListComponent implements OnInit {
  transacciones: any[] = [];
  seleccionados: number = 0;
  dataSource: MatTableDataSource<any> | undefined;
  subscription: Subscription = new Subscription();
  displayedColumns = [
    'id', 'createdAt', 'idUser', 'userId', 'ruta',
    'cuentaRetiroId', 'valorRetiro', 'bancoDestino', 'cuentaDestino', 'identificacion', 'acciones',
  ];
  emailOperador = '';
  expandedCardId: number | null = null;
  public currentPage = 1;
  public pageSize = 25;
  public totalItems = 0;
  public totalPages = this.totalItems / this.pageSize;
  private refreshInterval: any;
  montoMaxAutoShinkansen: number | null = null;
  /**
   * C1 (22/10/2026): true si falló `GET wallet-ves/retiros-ves/pending`. Las
   * FIAT_VES_CLP quedan sin `cardexId` (y con `cardexIdNoDisponible`) y el modal
   * de rechazo no las deja rechazar: sin `cardexId` caían a la lambda, que
   * abonaba los VES como CLP en `balances` además de devolver los VES.
   */
  errorCardexVes = false;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator | null = null;
  constructor(
    private transactionService: TransaccionService,
    private currencyPipe: CurrencyPipe,
    private clipboard: Clipboard,
    private spinner: SpinnerService,
    private dialog: MatDialog,
    private shinkansenPayoutService: ShinkansenPayoutService,
    private walletVesAdminService: WalletVesAdminService,
  ) { }
  async ngOnInit() {
    await this.loadMontoMaxAutoShinkansen();
    await this.loadTransactions();

    // Auto-refresh every 1 minute (60000 ms)
    this.refreshInterval = setInterval(() => {
      this.loadTransactions(false); // false = no mostrar spinner en auto-refresh
    }, 60000);

  }

  async loadTransactions(showSpinner: boolean = true) {
    const ref = showSpinner ? this.spinner.start() : null;

    try {
      const data = await this.transactionService.getAllWithdrawTransactions('POR_PROCESAR', this.currentPage, this.pageSize);

      this.transacciones = data.query;

      // Merge cardexId from wallet-ves pending for FIAT_VES_CLP transactions
      await this.mergeCardexIds();

      // Load payout status for each transaction
      await this.loadPayoutStatuses();

      // Filter out transactions that were already paid successfully via Shinkansen
      // Only show: no payout, pending, error, or reversed
      this.transacciones = this.transacciones.filter((transaction: any) => {
        if (!transaction.shinkansenPayout || !transaction.shinkansenPayout.exists) {
          return true; // Show if no payout exists
        }
        // Only hide if status is 'ok' (successfully paid)
        return transaction.shinkansenPayout.status !== 'ok';
      });

      this.dataSource = new MatTableDataSource(this.transacciones);
      this.totalItems = this.transacciones.length; // Update count after filtering
      this.totalPages = Math.ceil(this.totalItems / this.pageSize);
    } catch (error) {
    } finally {
      if (ref) {
        this.spinner.stop(ref);
      }
    }
  }

  /**
   * Merge cardexId from wallet-ves pending endpoint into FIAT_VES_CLP transactions
   */
  async mergeCardexIds(): Promise<void> {
    const vesTransactions = this.transacciones.filter((t: any) => t.tipoTransaccion === 'FIAT_VES_CLP');
    if (vesTransactions.length === 0) {
      this.errorCardexVes = false;
      return;
    }

    try {
      const pending = await firstValueFrom(this.walletVesAdminService.getPendingRetiros());
      const pendingList = Array.isArray(pending) ? pending : pending?.data ?? [];

      const cardexMap = new Map<number, string>();
      pendingList.forEach((item: any) => {
        if (item.transaccionId && item.cardexId) {
          cardexMap.set(item.transaccionId, item.cardexId);
        }
      });

      this.transacciones.forEach((t: any) => {
        if (t.tipoTransaccion === 'FIAT_VES_CLP' && cardexMap.has(t.id)) {
          t.cardexId = cardexMap.get(t.id);
        }
      });
      this.errorCardexVes = false;
    } catch (error) {
      // C1: antes el error se tragaba y el modal caía a la lambda. Ahora se
      // marcan las FIAT_VES_CLP (el modal no las rechaza) y se avisa en
      // pantalla; el toast sale solo al pasar a error, no en cada auto-refresh.
      console.error('[withdraw-trx-list] no se pudieron obtener los retiros VES pendientes', error);
      vesTransactions.forEach((t: any) => {
        t.cardexIdNoDisponible = true;
      });
      if (!this.errorCardexVes) {
        this.avisarErrorCardexVes();
      }
      this.errorCardexVes = true;
    }
  }

  private avisarErrorCardexVes(): void {
    Swal.mixin({
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 6000,
      timerProgressBar: true,
    }).fire({
      icon: 'warning',
      title: 'No se pudo obtener los retiros VES',
      text: 'Los retiros VES→CLP no se pueden rechazar. Recarga e intenta de nuevo.',
    });
  }

  /**
   * Load payout status for all transactions
   * Adds shinkansenPayout property to each transaction
   */
  async loadPayoutStatuses(): Promise<void> {
    if (!this.transacciones || this.transacciones.length === 0) {
      return;
    }


    // Create observables for all status requests
    const statusRequests = this.transacciones.map(transaction =>
      this.shinkansenPayoutService.getPayoutStatusByWalletTransactionId(String(transaction.id))
    );

    // If no requests, return early
    if (statusRequests.length === 0) {
      return;
    }

    try {
      // Execute all requests in parallel
      const statuses = await firstValueFrom(forkJoin(statusRequests));

      // Assign status to each transaction
      this.transacciones.forEach((transaction: any, index: number) => {
        transaction.shinkansenPayout = statuses[index];
      });

    } catch (error) {
      // Si hay error, marcar todas como sin payout
      this.transacciones.forEach((transaction: any) => {
        transaction.shinkansenPayout = { exists: false };
      });
    }
  }

  onPageChange(event: any) {
    this.pageSize = event.pageSize;
    this.currentPage = event.pageIndex + 1;
    this.loadTransactions();
  }

  toggleCard(id: number): void {
    this.expandedCardId = this.expandedCardId === id ? null : id;
  }

  openRejectTransactionModal(row: any) {
    const isMobile = window.innerWidth < 768;
    const dialogRef = this.dialog.open(RejectTransactionWithdrawModalComponent, {
      data: row,
      maxHeight: '90vh',
      maxWidth: isMobile ? '95vw' : '826px',
      minWidth: isMobile ? '90vw' : '800px',
      width: isMobile ? '95vw' : undefined,
    });
    dialogRef.afterClosed().subscribe(async (result) => {
      if (result === true) {
        this.loadTransactions();
      }
    });
  }

  copyData(data: any) {
    const id = data.id || '';
    const fechaCreacion = data.createdAt || '';
    const nombreUsuario = data.nombreUsuario + ' ' + data.apellidosUsuario || '';
    const aliasCuentaRetiro = data.alias || '';
    const montoRetiro = data.totalMonedaDestino || '';
    const bancoDestino = data.bancoNombre || '';
    const cuentaDestino = data.tipoCuentaNombre + ' ' + data.numeroCuenta || '';
    const identificacion = data.tipoIdentificacion + ' ' + data.numeroId || '';

    this.clipboard.copy(`Id: ${id}\n Fecha de Creacion: ${fechaCreacion}\n Nombre: ${nombreUsuario}\n Alias Cuenta Retiro: ${aliasCuentaRetiro}\n Monto Retiro: ${montoRetiro}\n Banco Destino: ${bancoDestino}\n Cuenta Destino: ${cuentaDestino}\n Identificacion: ${identificacion}`);
    const Toast = Swal.mixin({
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
    })

    Toast.fire({
      icon: 'success',
      title: 'Transferencia copiada al portapapeles'
    })
  }
  openSendTransactionModal(row: any) {
    const isMobile = window.innerWidth < 768;
    const dialogRef = this.dialog.open(SendTransactionWithdrawModalComponent, {
      data: row,
      maxHeight: '90vh',
      maxWidth: isMobile ? '95vw' : '826px',
      minWidth: isMobile ? '90vw' : '800px',
      width: isMobile ? '95vw' : undefined,
    });
    dialogRef.afterClosed().subscribe(async (result) => {
      if (result === true) {
        this.loadTransactions();
      }
      // if(result == true && this.usuarioID){
      //   this.listaCuentas = await this.accountRecipientService.getByUser(this.usuarioID);
      // }
    });
  }

  openEditTrxModal(row: any) {
    const isMobile = window.innerWidth < 768;
    const rowMapped = {
      alias: row.alias,
      codigoPais: row.codigoPais || row.paisDestino,
      tipoId: row.tipoIdentificacion,
      numeroId: row.numeroId,
      banco: row.bancoNombre,
      bancoId: row.bancoId,
      tipoCuentaId: row.tipoCuentaId,
      numeroCuenta: row.numeroCuenta,
      id: row.cuentaRetiroId
    };
    const dialogRef = this.dialog.open(AddWithdrawAccountsComponent, {
      data: {type: 'UPDATE', cuenta: rowMapped, userId: row.usuarioId},
      maxHeight: '90vh',
      maxWidth: isMobile ? '95vw' : '826px',
      minWidth: isMobile ? '90vw' : '800px',
      width: isMobile ? '95vw' : undefined,
    });
    dialogRef.afterClosed().subscribe(async (result) => {
      if (result === true) {
        this.loadTransactions();
      }
      // if(result == true && this.usuarioID){
      //   this.listaCuentas = await this.accountRecipientService.getByUser(this.usuarioID);
      // }
    });
  }

  openPayShinkansenModal(row: any) {

    const dialogRef = this.dialog.open(PayShinkansenModalComponent, {
      data: row,
      maxHeight: '95vh',
      maxWidth: '900px',
      width: '90%',
      panelClass: 'responsive-dialog'
    });
    dialogRef.afterClosed().subscribe(async (result) => {
      if (result === true) {
        this.loadTransactions();
      }
    });
  }

  openShinkansenMontoMaxModal(): void {
    const isMobile = window.innerWidth < 768;
    const ref = this.dialog.open(ShinkansenMontoMaxModalComponent, {
      maxWidth: isMobile ? '95vw' : '560px',
      minWidth: isMobile ? '90vw' : '480px',
    });
    ref.afterClosed().subscribe((updated) => {
      if (updated === true) {
        this.loadMontoMaxAutoShinkansen();
      }
    });
  }

  private async loadMontoMaxAutoShinkansen(): Promise<void> {
    try {
      const res = await firstValueFrom(this.shinkansenPayoutService.getMontoMaxAuto());
      this.montoMaxAutoShinkansen = res?.valor ?? null;
    } catch {
      this.montoMaxAutoShinkansen = null;
    }
  }

  private transaccionesIguales(transacciones1: Transaccion[], transacciones2: Transaccion[]): boolean {
    if (transacciones1.length !== transacciones2.length) {
      return false; // Si las longitudes son diferentes, las listas no son iguales
    }
    const transacciones1Ids = transacciones1.map((t) => t.id);
    const transacciones2Ids = transacciones2.map((t) => t.id);

    // Compara los identificadores de las transacciones
    for (const id of transacciones1Ids) {
      if (!transacciones2Ids.includes(id)) {
        return false; // Si un identificador en transacciones1 no está en transacciones2, no son iguales
      }
    }
    return true; // Si todas las comparaciones fueron iguales, las listas son iguales
  }

  change() {
    this.seleccionados =
      this.dataSource?.data.filter((value) => value.seleccionado).length || 0;

  }

  limpiarSeleccion() {
    const data =
      this.dataSource?.data.map((value) => {
        return { ...value, seleccionado: false };
      }) || [];

    if (this.dataSource && this.dataSource.data) {
      this.dataSource.data = data;
    }
    this.change();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();

    // Clear auto-refresh interval
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    if (this.dataSource) {
      this.dataSource.filter = filterValue.trim().toUpperCase();
    }
  }

  public doFilter = (event: any) => {
    const element = event.currentTarget as HTMLInputElement;
    const value = element.value;
    if (this.dataSource)
      this.dataSource.filter = value.trim().toLocaleLowerCase();
  };
  //TPDP

  returnSelectedTransactions(row: Transaccion) {
    const selected =
      this.dataSource?.data?.filter((x) => x.seleccionado && x.id !== row.id) ||
      [];
    selected.push(row);
    return selected;
  }

  disableButton(email: string) {
    if (this.emailOperador !== email) return true;
    return false;
  }

  /**
   * Check if Shinkansen pay button should be shown
   * Show only if: no payout exists OR payout failed (can retry)
   */
  canPayWithShinkansen(row: any): boolean {
    if (this.exceedsShinkansenAutoLimit(row)) return false;

    if (!row.shinkansenPayout) return true; // No status loaded yet, show button

    const payout = row.shinkansenPayout;

    // Show if no payout exists
    if (!payout.exists) return true;

    // Show if can retry (error or reversed)
    if (payout.canRetry) return true;

    // Hide in all other cases (ok, pending)
    return false;
  }

  exceedsShinkansenAutoLimit(row: any): boolean {
    if (this.montoMaxAutoShinkansen === null || this.montoMaxAutoShinkansen <= 0) {
      return false;
    }
    const monto = Number(row?.totalMonedaDestino);
    if (!Number.isFinite(monto) || monto <= 0) return false;
    return monto > this.montoMaxAutoShinkansen;
  }

  /**
   * Check if manual process button should be shown.
   *
   * Mostrar manual cuando:
   *  - No hay payout cargado todavía (estado inicial)
   *  - No existe payout (orden nueva, operador puede elegir manual o Shinkansen)
   *  - status === 'manual_pending': el SISTEMA decidió manual (límite excedido,
   *    validación falla pre-envío, network error). NO disparó SQS sync.
   *    Es seguro procesar manual sin race condition.
   *
   * Ocultar cuando:
   *  - status === 'pending': pago in-flight, esperar webhook
   *  - status === 'ok': ya pagado
   *  - status === 'error' / 'reversed': WalletTransactionSyncService va a procesar
   *    automáticamente un rejectWithdraw vía SQS → lambda. Si el operador hace
   *    manual antes del sync, se duplica el flujo (TX queda ENVIADA + lambda
   *    intenta rechazar). Para reintentar estos casos, el operador usa el botón
   *    "Pagar Shinkansen" (canRetry, nuevos IDs).
   */
  canProcessManually(row: any): boolean {
    if (!row.shinkansenPayout) return true; // No status loaded yet, show button

    const payout = row.shinkansenPayout;
    if (!payout.exists) return true;

    return payout.status === 'manual_pending';
  }

  /**
   * Horas que un payout puede permanecer en 'pending' antes de considerarse
   * "clavado" (Shinkansen nunca devolvió estado final). Shinkansen normalmente
   * resuelve en < 1h; pasado este umbral se permite el rechazo manual desde el UI.
   *
   * ⚠️ OJO: rechazar un pending NO cancela el payout en Shinkansen. Si Shinkansen
   * paga después, hay riesgo de doble movimiento (cliente reembolsado + pago que
   * igual sale). Usar solo cuando se confirmó que el pago no va a salir.
   */
  private readonly STALE_PENDING_REJECT_HOURS = 3;

  /** true si el payout está 'pending' y ya superó el umbral de "clavado". */
  isStalePending(row: any): boolean {
    const payout = row?.shinkansenPayout;
    if (!payout?.exists || payout.status !== 'pending' || !payout.createdAt) {
      return false;
    }
    const created = new Date(payout.createdAt).getTime();
    if (!Number.isFinite(created)) return false;
    const hours = (Date.now() - created) / 3_600_000;
    return hours >= this.STALE_PENDING_REJECT_HOURS;
  }

  /**
   * El botón Rechazar solo se bloquea si el payout está 'pending' y todavía es
   * reciente (in-flight). Un pending "clavado" (> STALE_PENDING_REJECT_HOURS) se
   * puede rechazar manualmente.
   */
  isRejectBlocked(row: any): boolean {
    return row?.shinkansenPayout?.status === 'pending' && !this.isStalePending(row);
  }

  /**
   * Get status badge info for display
   */
  getPayoutStatusBadge(row: any): { text: string; class: string; icon: string } | null {
    if (!row.shinkansenPayout || !row.shinkansenPayout.exists) {
      return null; // No badge if no payout
    }

    const status = row.shinkansenPayout.status;

    switch (status) {
      case 'ok':
        return {
          text: 'Pagado Shinkansen',
          class: 'badge-success',
          icon: '✅'
        };
      case 'pending':
        return {
          text: 'En proceso',
          class: 'badge-warning',
          icon: '🟡'
        };
      case 'error':
        return {
          text: 'Error (puede reintentar)',
          class: 'badge-error',
          icon: '❌'
        };
      case 'reversed':
        return {
          text: 'Reversado',
          class: 'badge-info',
          icon: '↩️'
        };
      case 'manual_pending':
        return {
          text: 'Pago manual pendiente',
          class: 'badge-warning',
          icon: '⏳'
        };
      default:
        return {
          text: status,
          class: 'badge-default',
          icon: '❓'
        };
    }
  }

  getPayoutErrorDisplay(row: any): string | null {
    const payout = row?.shinkansenPayout;
    if (!payout || !payout.exists) return null;
    if (payout.status !== 'error' && payout.status !== 'reversed') return null;
    return payout.errorMessage || payout.friendlyMessage || null;
  }

  getPayoutErrorTooltip(row: any): string {
    const payout = row?.shinkansenPayout;
    if (!payout || !payout.exists) return '';
    const parts: string[] = [];
    if (payout.errorCode) parts.push(`Código: ${payout.errorCode}`);
    if (payout.errorMessage) parts.push(`Mensaje: ${payout.errorMessage}`);
    if (payout.shinkansenTransactionId) parts.push(`Tx Shinkansen: ${payout.shinkansenTransactionId}`);
    return parts.join(' | ');
  }
}
